import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, asArray } from "@/lib/api";
import { fmtTime } from "@/lib/time";
import { toast } from "sonner";
import { Server, Search, Building2 } from "lucide-react";
import { StatusBadge } from "@/components/Primitives";

// All managed devices, filterable by assignment. Unassigned devices can be
// grouped under a client inline (single or bulk); each row opens the device.

export default function Devices() {
  const [agents, setAgents] = useState(null);
  const [clients, setClients] = useState([]);
  const [filter, setFilter] = useState("all"); // all | unassigned | <client_id>
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(new Set());
  const [bulkClient, setBulkClient] = useState("");
  const nav = useNavigate();

  const load = () => Promise.all([
    api.get("/agents").then(r => setAgents(asArray(r.data))),
    api.get("/clients").then(r => setClients(asArray(r.data))),
  ]).catch(() => setAgents([]));
  useEffect(() => { load(); const t = setInterval(load, 12000); return () => clearInterval(t); }, []);

  const clientName = (id) => clients.find(c => c.id === id)?.name;

  const shown = (agents || []).filter(a => {
    if (filter === "unassigned" && a.client_id) return false;
    if (filter !== "all" && filter !== "unassigned" && a.client_id !== filter) return false;
    if (q && !(`${a.hostname} ${a.os} ${a.manufacturer || ""} ${a.model || ""} ${a.serial_number || ""} ${a.ip || ""} ${clientName(a.client_id) || ""}`.toLowerCase().includes(q.toLowerCase()))) return false;
    return true;
  });

  const counts = {
    all: (agents || []).length,
    unassigned: (agents || []).filter(a => !a.client_id).length,
  };

  const assignOne = async (agentId, clientId) => {
    if (!clientId) return;
    try { await api.post(`/agents/${agentId}/assign`, { client_id: clientId }); toast.success("Assigned"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const bulkAssign = async () => {
    if (!bulkClient || sel.size === 0) { toast.error("Pick devices and a client"); return; }
    try {
      await Promise.all([...sel].map(id => api.post(`/agents/${id}/assign`, { client_id: bulkClient })));
      toast.success(`Assigned ${sel.size} device(s)`); setSel(new Set()); setBulkClient(""); await load();
    } catch { toast.error("Some assignments failed"); }
  };
  const toggle = (id) => setSel(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  return (
    <div className="p-6 md:p-8 max-w-[1200px] mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-blue-500/10 border border-blue-500/30 flex items-center justify-center"><Server className="text-blue-400" size={18} /></div>
          <div>
            <h1 className="text-xl font-semibold text-zinc-50">Devices</h1>
            <div className="text-xs text-zinc-500 uppercase tracking-widest font-mono">All managed endpoints · hardware · assignment</div>
          </div>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-2.5 text-zinc-600" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Filter…" className="bg-zinc-950 border border-zinc-800 rounded-md pl-8 pr-3 py-1.5 text-sm text-zinc-200 outline-none focus:border-zinc-600" />
        </div>
      </div>

      {/* filters */}
      <div className="flex items-center gap-2 flex-wrap mb-4">
        <FilterChip active={filter === "all"} onClick={() => setFilter("all")}>All <span className="text-zinc-500">{counts.all}</span></FilterChip>
        <FilterChip active={filter === "unassigned"} onClick={() => setFilter("unassigned")}>Unassigned <span className="text-amber-400">{counts.unassigned}</span></FilterChip>
        <select value={["all", "unassigned"].includes(filter) ? "" : filter} onChange={e => setFilter(e.target.value || "all")}
          className="bg-zinc-950 border border-zinc-800 rounded-md px-3 py-1.5 text-sm text-zinc-300">
          <option value="">By client…</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.name} ({c.agent_count || 0})</option>)}
        </select>
      </div>

      {/* bulk assign bar */}
      {sel.size > 0 && (
        <div className="flex items-center gap-2 bg-blue-500/10 border border-blue-500/30 rounded-md px-3 py-2 mb-3 text-sm">
          <span className="text-blue-200">{sel.size} selected</span>
          <select value={bulkClient} onChange={e => setBulkClient(e.target.value)} className="bg-zinc-900 border border-zinc-800 rounded px-2.5 py-1.5 text-xs text-zinc-200">
            <option value="">Assign to client…</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <button onClick={bulkAssign} className="bg-blue-600 hover:bg-blue-700 text-white rounded px-3 py-1.5 text-xs">Assign</button>
          <button onClick={() => setSel(new Set())} className="text-zinc-400 hover:text-zinc-200 text-xs ml-1">Clear</button>
        </div>
      )}

      {agents === null ? <div className="text-zinc-500 text-sm">Loading…</div> :
        shown.length === 0 ? <div className="bg-zinc-950 border border-zinc-900 border-dashed rounded-md p-10 text-center text-zinc-500 text-sm">No devices match.</div> :
        <div className="bg-zinc-950 border border-zinc-900 rounded-md overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900/40 border-b border-zinc-900 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
              <tr>
                <th className="w-8 px-3 py-3"></th>
                <th className="text-left px-4 py-3 font-medium">Device</th>
                <th className="text-left px-4 py-3 font-medium">OS</th>
                <th className="text-left px-4 py-3 font-medium">Client</th>
                <th className="text-left px-4 py-3 font-medium">IP</th>
                <th className="text-left px-4 py-3 font-medium">Last seen</th>
                <th className="text-left px-4 py-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {shown.map(a => (
                <tr key={a.id} className="border-b border-zinc-900/60 hover:bg-zinc-900/40">
                  <td className="px-3 py-2.5" onClick={e => e.stopPropagation()}>
                    {!a.client_id && <input type="checkbox" checked={sel.has(a.id)} onChange={() => toggle(a.id)} />}
                  </td>
                  <td className="px-4 py-2.5 text-zinc-100 font-medium cursor-pointer" onClick={() => nav(`/agents/${a.id}`)}>
                    {a.hostname}
                    {(a.manufacturer || a.model || a.serial_number) && (
                      <div className="text-[11px] text-zinc-600 font-normal">
                        {[a.manufacturer, a.model].filter(Boolean).join(" ")}
                        {a.serial_number ? ` · ${a.serial_number}` : ""}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-zinc-400 cursor-pointer" onClick={() => nav(`/agents/${a.id}`)}>{a.os} {a.os_version}</td>
                  <td className="px-4 py-2.5">
                    {a.client_id ? (
                      <span className="inline-flex items-center gap-1.5 text-zinc-300"><Building2 size={12} className="text-zinc-500" />{clientName(a.client_id) || a.client_id}</span>
                    ) : (
                      <select defaultValue="" onChange={e => assignOne(a.id, e.target.value)} className="bg-zinc-900 border border-amber-500/30 rounded px-2 py-1 text-xs text-amber-300">
                        <option value="">Unassigned — assign…</option>
                        {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    )}
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs text-zinc-500 cursor-pointer" onClick={() => nav(`/agents/${a.id}`)}>{a.ip || "—"}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-zinc-500 cursor-pointer" onClick={() => nav(`/agents/${a.id}`)}>{a.last_heartbeat ? fmtTime(a.last_heartbeat) : "—"}</td>
                  <td className="px-4 py-2.5 cursor-pointer" onClick={() => nav(`/agents/${a.id}`)}><StatusBadge status={a.status === "online" ? "online" : "offline"} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      }
    </div>
  );
}

function FilterChip({ active, onClick, children }) {
  return (
    <button onClick={onClick} className={`rounded-md px-3 py-1.5 text-sm border ${active ? "bg-blue-500/15 border-blue-500/40 text-blue-200" : "bg-zinc-950 border-zinc-800 text-zinc-400 hover:text-zinc-200"}`}>{children}</button>
  );
}
